import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../App';
import apiClient from '../../utils/apiClient';
import { Handshake,
  LayoutDashboard, Users, Building2, Mail, Activity,
  Server, ChevronLeft, ChevronRight, LogOut, Shield,
  ArrowLeft, Menu, X, TrendingUp, Brain, Wifi, BarChart3, Database, Wrench, CreditCard, Globe2, Bot, Library, Funnel, Megaphone, AlertCircle, ShieldCheck
} from 'lucide-react';
import { Button } from '../ui/button';
import BackButton from '../ui/back-button';
import { toast } from 'sonner';
import '../../styles/admin-light.css';
import { BackToTop } from '../BackToTop';

const adminNavSections = [
  {
    title: 'Operate',
    items: [
      { path: '/admin', label: 'Command Center', icon: LayoutDashboard, end: true },
      { path: '/admin/users', label: 'Users', icon: Users },
      { path: '/admin/businesses', label: 'Businesses', icon: Building2 },
      { path: '/admin/escalations', label: 'Escalations', icon: AlertCircle },
      { path: '/admin/support', label: 'Support Tools', icon: Wrench },
      { path: '/admin/parser-rules', label: 'Parser Rules', icon: Wrench },
    ],
  },
  {
    title: 'Grow',
    items: [
      { path: '/admin/revenue', label: 'Revenue & Billing', icon: CreditCard },
      { path: '/admin/partners', label: 'Partners & Channels', icon: Handshake },
      { path: '/admin/ai-analytics', label: 'AI & Automation', icon: Brain },
      { path: '/admin/comms', label: 'Comms', icon: Megaphone },
    ],
  },
  {
    title: 'Platform',
    items: [
      { path: '/admin/platform-health', label: 'Platform Health', icon: Server },
      { path: '/admin/integrations', label: 'Integrations', icon: Wifi },
      { path: '/admin/bank-feeds', label: 'Bank Feeds', icon: Database },
      { path: '/admin/financial-ops', label: 'Accounting Integrity', icon: TrendingUp },
    ],
  },
  {
    title: 'Configure',
    items: [
      { path: '/admin/country-requests', label: 'Country Requests', icon: Globe2 },
      { path: '/admin/master-tax', label: 'Master Tax', icon: Library },
      { path: '/admin/rule-watcher', label: 'Payroll Rule Watcher', icon: Bot },
      { path: '/admin/recon-integrity', label: 'Recon Integrity', icon: ShieldCheck },
      { path: '/admin/kb-screenshots', label: 'KB Screenshots', icon: Library },
      { path: '/admin/merchant-kb', label: 'Merchant KB', icon: Database },
    ],
  },
];

const adminNavItems = adminNavSections.flatMap(s => s.items);

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(null);

  const handleLogout = () => {
    // Clear auth state first, then redirect
    logout().finally(() => {
      window.location.replace('/auth');
    });
  };

  useEffect(() => {
    apiClient.get('/admin/check')
      .then(() => setIsAdmin(true))
      .catch(() => {
        setIsAdmin(false);
        toast.error('You do not have admin access');
        navigate('/dashboard');
      });
  }, [navigate]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Brand the Radix portals (dialogs, drawers, menus) while the admin console is mounted.
  useEffect(() => {
    document.body.classList.add('akf-admin');
    return () => document.body.classList.remove('akf-admin');
  }, []);

  if (isAdmin === null) {
    return (
      <div className="flex items-center justify-center min-h-dvh bg-[#F8FAFC]">
        <div className="flex items-center gap-3 text-slate-500"><Shield className="w-5 h-5 text-[#108981] animate-pulse" /><span className="animate-pulse">Verifying admin access…</span></div>
      </div>
    );
  }

  if (!isAdmin) return null;

  const sidebarWidth = collapsed ? 'w-[68px]' : 'w-[240px]';

  return (
    <div className="flex min-h-dvh bg-[#F8FAFC]" data-testid="admin-shell">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:sticky lg:top-0 lg:h-dvh inset-y-0 left-0 z-50 ${sidebarWidth} bg-[#1B2C66] text-white flex flex-col transition-all duration-200 ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} data-testid="admin-sidebar">
        {/* Header */}
        <div className="h-[60px] flex items-center px-4 gap-2.5 border-b border-white/10">
          <span className="w-8 h-8 rounded-xl bg-[#108981] flex items-center justify-center shrink-0 shadow-[0_0_0_4px_rgba(95,198,197,0.18)]"><Shield className="w-4 h-4 text-white" /></span>
          {!collapsed && <div className="min-w-0 leading-tight"><p className="text-[15px] font-semibold tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>Akount<span className="text-[#5FC6C5]">Fly</span></p><p className="text-[10px] uppercase tracking-[0.18em] text-white/50">Admin console</p></div>}
          <button onClick={() => setMobileOpen(false)} className="ml-auto lg:hidden text-white/60 hover:text-white p-1" aria-label="Close menu"><X className="w-4 h-4" /></button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 px-2 overflow-y-auto" data-testid="admin-sidebar-nav">
          {adminNavSections.map(section => (
            <div key={section.title} className="mb-2">
              {!collapsed && (
                <p className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#5FC6C5]/80">{section.title}</p>
              )}
              <div className="space-y-0.5">
                {section.items.map(item => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.end}
                    title={collapsed ? item.label : undefined}
                    className={({ isActive }) => `relative flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] transition-colors ${isActive ? 'bg-white/10 text-white font-semibold before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-full before:bg-[#5FC6C5]' : 'text-white/70 hover:text-white hover:bg-white/5'}`}
                    data-testid={`admin-nav-${item.label.toLowerCase().replace(/[\s&]+/g, '-')}`}
                  >
                    <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="border-t border-white/10 p-3 space-y-1">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors"
            data-testid="admin-back-to-app"
          >
            <ArrowLeft className="w-4 h-4" />
            {!collapsed && <span>Back to App</span>}
          </button>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-white/40 hover:text-white/80 transition-colors"
            data-testid="admin-sidebar-collapse"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <><ChevronLeft className="w-4 h-4" /><span>Collapse</span></>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 admin-light">
        {/* Top bar. Shows a compact "<" back button on every route other
            than the Admin root so admins can pop navigation the same way
            they used to. */}
        <header className="h-[60px] bg-white/85 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-3 lg:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden text-slate-500 hover:text-slate-900 p-1">
              <Menu className="w-5 h-5" />
            </button>
            {location.pathname !== '/admin' && (
              <BackButton fallbackPath="/admin" className="h-9 w-9 shrink-0" data-testid="global-back-button-admin" />
            )}
            {(() => {
              const sec = adminNavSections.find(sct => sct.items.some(n => location.pathname === n.path || (!n.end && location.pathname.startsWith(n.path + '/'))));
              const item = adminNavItems.find(n => location.pathname === n.path || (!n.end && location.pathname.startsWith(n.path + '/')));
              return (
                <div className="min-w-0 leading-tight">
                  {sec && <p className="text-[10px] uppercase tracking-[0.18em] text-[#108981] font-semibold hidden sm:block">{sec.title}</p>}
                  <h1 className="text-[15px] font-semibold text-[#1B2C66] truncate" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>{item?.label || 'Admin'}</h1>
                </div>
              );
            })()}
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-slate-600 truncate max-w-[220px] bg-[#eef2f7] rounded-full px-2.5 py-1"><span className="w-1.5 h-1.5 rounded-full bg-[#108981]" />{user?.email}</span>
            <button
              onClick={handleLogout}
              className="text-slate-500 hover:text-red-600 p-2 rounded-md hover:bg-slate-100 transition-colors"
              data-testid="admin-logout-btn"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="p-4 lg:p-6 max-w-[1600px] mx-auto">
          <Outlet />
        </div>
      </main>
      <BackToTop desktopLeftClass="lg:left-[calc(240px+1.5rem)]" />
    </div>
  );
}

